import DeleteForeverIcon from '@mui/icons-material/DeleteForever'
import SettingsIcon from '@mui/icons-material/Settings'
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart'
import TableChartIcon from '@mui/icons-material/TableChart'
import TabContext from '@mui/lab/TabContext'
import TabList from '@mui/lab/TabList'
import TabPanel from '@mui/lab/TabPanel'
import {LinearProgress, Tooltip} from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import {blue, deepOrange, green, grey} from '@mui/material/colors'
import Divider from '@mui/material/Divider'
import Grid from '@mui/material/Grid'
import {styled} from '@mui/material/styles'
import Tab from '@mui/material/Tab'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell, {tableCellClasses} from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import BasicDrawer from 'app/components/drawers/BasicDrawer'
import EmptyStateCard from 'app/components/emptyStates/emptyFolder'
import {useGetStripePrices} from 'app/hooks/subscription/useGetStripePrices'
import usePostStripeStartPortal from 'app/hooks/subscription/usePostStripeStartPortal'
import {useAuthProvider} from 'app/modules/auth'
import * as React from 'react'
import {useEffect, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import toastr from 'toastr'
import {emGetActiveTenantData} from 'utilities/emUserFeatures'
import {emFormatToCurrency, emToAbsoluteUrl} from 'utilities/extensionMethods'
import PaymentCheckoutPlan from './PaymentCheckoutPlan'
import QuantityButton from './QuantityButton'
import {ProductResult} from './SubscriptionPlans'
import UpgradePricingTable from './UpgradePricingTable'

export default function ShoppingCartSubscriptions({
  tenantSubPlans,
  makeTeam,
}: {
  tenantSubPlans?: any[]
  makeTeam?: React.Dispatch<React.SetStateAction<boolean>>
}): React.ReactElement {
  const {currentUser, reduxUser} = useAuthProvider()
  const personalTenant = reduxUser?.subscriptions?.find((f) => f.tenant?.is_personal)
  const activeTenant = currentUser ? emGetActiveTenantData(currentUser) : undefined
  const navigate = useNavigate()
  const queryGetProducts = useGetStripePrices()
  const [execPortal, {data: portalData, error: portalError, loading: portalLoading}] =
    usePostStripeStartPortal()
  const [tenantStripeCustomerId, setTenantStripeCustomerId] = React.useState<any>()
  const [productList, setProductList] = useState<ProductResult[]>([])
  const [showCart, setShowCart] = useState<boolean>(false)
  const [qtyArr, setQtyArr] = useState<{id: string; seat_quantity: number}[]>([])
  const [cartItems, setCartItems] = useState<any>()

  const handleChangeQty = (id: string, seat_quantity: number) => {
    const qtyNew = qtyArr.filter((f) => f.id != id)
    setQtyArr((prev) => [...qtyNew, {id: id, seat_quantity: seat_quantity}])
  }

  const handleChangeQtyInCart = (id: string, qty: number) => {
    setQtyArr((prev) => [...prev.filter((f) => f.id != id), {id: id, seat_quantity: qty}])
    cartItems?.map((m: any) => {
      if (m.id === id) {
        m.seat_quantity = qty
      }
    })
  }

  const StyledTableCell = styled(TableCell)(({theme}) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: grey[400],
      color: theme.palette.common.black,
      fontWeight: 700,
      fontSize: 16,
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 14,
    },
  }))

  const StyledTableRow = styled(TableRow)(({theme}) => ({
    verticalAlign: 'baseline',
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.action.hover,
    },
    // hide last border
    '&:last-child td, &:last-child th': {
      border: 0,
    },
  }))

  function createData2(
    id: string,
    name: string,
    prices: any[],
    seats: React.ReactElement,
    cart: React.ReactElement
  ) {
    return {id, name, prices, cart, seats}
  }

  //this gets the available products and if the tenant has any subscriptions and the number of seats
  const tenants_existing_subscription_plans = queryGetProducts?.data?.filter(
    (f: any) => f.total_existing_subs > 0
  )
  const isPersonalTenant = tenantSubPlans?.find((f) => f.tenant.is_personal)
  const [activeTab, setActiveTab] = useState<string>(isPersonalTenant ? 'individual' : 'team')
  const handleChangeTab = (event: React.SyntheticEvent, newValue: string) => {
    setActiveTab(newValue)
  }

  const rows = React.useMemo(() => {
    const r: any[] = []
    if (productList && Array.isArray(productList)) {
      productList?.map((p, i) => {
        r.push(
          createData2(
            p.id,
            p.name,
            p.prices.sort((a: any, b: any) => a.base_price - b.base_price),
            <QuantityButton
              key={`${p.id}-qty`}
              value={qtyArr.find((f) => f.id === p.id)?.seat_quantity ?? 0}
              id={p.id}
              onChange={handleChangeQty}
            />,
            <Tooltip title={'Add to Cart'}>
              <Button
                key={`${p.id}-btn`}
                color={'success'}
                onClick={() =>
                  handleAddToCart(
                    p,
                    isPersonalTenant ? 1 : qtyArr.find((f) => f.id === p.id)?.seat_quantity ?? 0
                  )
                }
                variant={'contained'}
                size='small'
                sx={{
                  position: 'inherit',
                  color: 'black',
                  textTransform: 'none',
                  fontWeight: 400,
                  width: '150px',
                  background: '#ffd814',
                  borderColor: '#fcd200',
                  borderRadius: '100px',
                  '&:hover': {
                    bgcolor: '#ffd814',
                    borderColor: '#fcd200',
                  },
                }}
                disabled={activeTab === 'individual' && !isPersonalTenant}
              >
                {'Add To Cart'}
              </Button>
            </Tooltip>
          )
        )
      })
    }
    return r
  }, [productList, qtyArr, isPersonalTenant])

  useEffect(() => {
    if (queryGetProducts?.data) {
      console.log(`Loading ProductList.`)
      setProductList(queryGetProducts?.data.map((item: ProductResult) => item))
    }
    // if (tenants_existing_subscription_plans?.find((f: any) => f.total_existing_subs > 0)) {
    //   //this is a personal tenant, so we can set the tenantStripeCustomerId
    //   setTenantStripeCustomerId(
    //     tenants_existing_subscription_plans?.find((f: any) => f.total_existing_subs > 0)?.tenant
    //       .stripe_customer_id
    //   )
    // }
  }, [queryGetProducts?.data])

  const getPlanPrice = (prices: any[], seats: number) => {
    //find price to use based on # of seats

    if (prices && prices.length > 0) {
      let price_per_seat = 0
      let base_price = 0
      let min_seats = 0
      //will continue to set the price until it doesn't
      prices.map((p) => {
        if (seats >= p.min_seats) {
          min_seats = p.min_seats
          price_per_seat = p.per_seat_price
          base_price = p.min_price
        }
      })
      const total_price = base_price + (price_per_seat ? price_per_seat * (seats - min_seats) : 0)

      return total_price
    }
    return 0
  }

  function handleAddToCart(plan: any, seats: number) {
    //putting this in her for now so we only can do 1 plan at a time
    if (!cartItems || cartItems.length === 0) {
      if (seats === 0) {
        toastr.warning(`Please select a quantity of seats`)
        return
      } else {
        if (plan.id) {
          const foundItem = cartItems?.find((f: any) => f.id === plan.id)
          if (foundItem) {
            foundItem.seat_quantity = seats
            const newArr = [foundItem]
            setCartItems((prev: any) => [...(prev ?? []), ...newArr])
          } else {
            const newArr = [{plan: plan, seat_quantity: seats}]
            setCartItems((prev: any) => [...(prev ?? []), ...newArr])
          }
          toastr.success(`Added plan to your cart`)
        }
      }
    } else {
      toastr.warning(`You already have a plan in your cart, please remove it first`)
    }
  }

  const handlRemoveFromCart = (id: string) => {
    const findit = cartItems?.find((f: any) => f.plan.id === id)
    const newArr = cartItems ? [...cartItems.filter((f: any) => f.plan.id != id)] : []
    setCartItems(newArr)
    findit && toastr.warning(`Removed plan from your cart`)
  }

  const handleGoToCustomerPortal = async () => {
    execPortal(
      {customer_id: tenantStripeCustomerId},
      {
        onComplete: (response: any) => {
          window.location.href = response.url
        },
        onError: (error: any) => {
          toastr.error('An error occurred on retrieving stripe customer portal')
        },
      }
    )
  }

  const memoedTenantPaymentButton = React.useMemo(() => {
    let node = <></>
    if (tenantStripeCustomerId) {
      node = (
        <Button
          size='small'
          sx={{fontWeight: 700, mb: 1, bgcolor: green[50], color: green[900]}}
          variant={'text'}
          onClick={() => handleGoToCustomerPortal()}
          color={'success'}
        >
          Manage Subscriptions
        </Button>
      )
    }

    return node
  }, [tenantStripeCustomerId])

  return (
    <>
      {' '}
      <Box height={'65px'} bgcolor={grey[50]} display={'flex'}>
        <Box
          display={'flex'}
          flexDirection={'row'}
          alignItems={'center'}
          justifyContent={'space-between'}
        >
          <Box display={'flex'} flexDirection={'row'} alignItems={'center'}>
            <Button onClick={() => navigate('/account/settings?tab=subscriptions')}>
              <SettingsIcon color={'primary'} />
            </Button>
            <Box display={'flex'}>
              <Typography variant={'h5'} fontWeight={700}>
                Current Subscriptions for Tenant:
              </Typography>
              <Typography variant={'h5'} fontWeight={700} color={grey[600]} pl={1}>
                {currentUser?.activeTenantData?.name}
              </Typography>
            </Box>
            {tenants_existing_subscription_plans?.map((pl: any, i: number) => {
              if (pl.total_existing_subs > 0) {
                //this plan is a purchased plan
                return (
                  <Box
                    key={`boxfp_${i}`}
                    display={'flex'}
                    ml={2}
                    boxShadow={
                      'rgba(0, 0, 0, 0.3) 0px 1px 1px, rgba(0, 0, 0, 0.2) 0px 1px 1px 0px, rgba(0, 0, 0, 0.1) 0px -1px 0px inset'
                    }
                    bgcolor={'white'}
                    borderRadius={1}
                  >
                    <Typography variant={'body1'} fontWeight={700} p={1}>
                      {pl.name} {`with: ${Object.values(pl.existing_subs)} seats`}
                    </Typography>
                  </Box>
                )
              } else {
                return (
                  <Typography
                    key={`asdf_${i}`}
                    variant={'h6'}
                    fontStyle={'italic'}
                    fontWeight={700}
                    p={2}
                  >
                    {`No paid subscriptions found`}
                  </Typography>
                )
              }
            })}
          </Box>

          <Box display={'flex'} ml={2} justifyContent={'flex-end'}>
            {isPersonalTenant && (
              <Typography variant={'body1'} fontStyle={'italic'} p={2} color={deepOrange[900]}>
                This is your personal Tenant
              </Typography>
            )}

            {memoedTenantPaymentButton}
          </Box>
        </Box>
      </Box>
      <Grid container maxWidth={'1320px'} display={'flex'} margin={'auto'}>
        <BasicDrawer
          anchor={'right'}
          title={'Shopping Cart'}
          IconBGColor={'white'}
          IconBoxShadow='rgba(0, 0, 0, 0.4) 0px 2px 4px, rgba(0, 0, 0, 0.3) 0px 7px 13px -3px, rgba(0, 0, 0, 0.2) 0px -3px 0px inset;'
          handle={
            <Box display={'flex'} flexDirection={'row'} alignItems={'center'} mr={3} ml={1}>
              <Box>
                <TableChartIcon sx={{bgcolor: 'white', fontSize: '28px'}} />
              </Box>
              <Box display={'flex'} flexDirection={'column'} ml={1} mr={2}>
                <Typography variant={'caption'} color={grey[800]} fontWeight={700}>
                  Plan
                </Typography>
                <Typography variant={'caption'} color={grey[800]} fontWeight={700}>
                  Features
                </Typography>
              </Box>
            </Box>
          }
          node={
            <Box
              display={'flex'}
              justifyContent={'space-between'}
              alignItems={'center'}
              p={2}
              bgcolor={'white'}
              borderBottom={1}
              borderColor={'grey.400'}
            >
              <UpgradePricingTable
                currentPlan={
                  tenants_existing_subscription_plans?.length
                    ? tenants_existing_subscription_plans?.find((f: any) =>
                        f.name?.includes('Standard')
                      )
                      ? 'standard'
                      : tenants_existing_subscription_plans?.find((f: any) =>
                          f.name?.includes('Premium')
                        )
                      ? 'premium'
                      : 'free'
                    : ''
                }
              />
            </Box>
          }
        />
        {!showCart ? (
          <Grid item xs={12}>
            <TabContext value={activeTab}>
              <TabList
                onChange={handleChangeTab}
                textColor='inherit'
                variant='scrollable'
                sx={{minWidth: '15em'}}
              >
                <Tab
                  label='Personal Tenant Plans'
                  value={'individual'}
                  sx={{alignItems: 'start'}}
                />
                <Tab label='Team Tenant Plans' value={'team'} sx={{alignItems: 'start'}} />
              </TabList>
              <TabPanel value={'individual'} sx={{padding: 0, width: '100%'}}>
                <Table
                  aria-label='customized table'
                  sx={{
                    '& .MuiTableRow-root': {
                      position: 'inherit',
                    },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <StyledTableCell width={'12%'}>{'Subscription Plans'}</StyledTableCell>
                      <StyledTableCell width={'25%'}>Prices</StyledTableCell>

                      <StyledTableCell align='right' width={'12%'}>
                        <Button
                          sx={{
                            color: blue[900],
                            fontWeight: 700,
                            bgcolor: '#FFFFFF',
                            '&:hover': {bgcolor: grey[50]},
                            position: 'inherit',
                          }}
                          disabled={cartItems?.length === 0}
                          onClick={() => setShowCart(true)}
                          size={'small'}
                          variant={'contained'}
                          startIcon={<ShoppingCartIcon />}
                        >
                          {/* get the number of qty from the array of objects */}
                          View Cart: {cartItems?.length ?? 0}
                        </Button>
                      </StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* put in a tab for basic one for add */}
                    {rows.map((row, i) => {
                      const today = new Date()
                      const modifyDate = new Date(today)
                      const isPurchased = tenants_existing_subscription_plans?.find(
                        (f: any) => f.id === row.id
                      )

                      return (
                        <StyledTableRow key={`row_${i}`} sx={{position: 'relative'}}>
                          <StyledTableCell component='th' scope='row'>
                            <Typography variant={'body1'} fontWeight={700}>
                              {row.name ?? 'dfgd'}
                            </Typography>
                            {isPurchased?.id ? (
                              <Typography variant={'body1'} color={green[600]}>
                                Currently Subscribed
                              </Typography>
                            ) : (
                              <></>
                            )}
                          </StyledTableCell>
                          <StyledTableCell>
                            {row.prices?.map((p: any, i: number) => {
                              if (i === 0) {
                                return (
                                  <Box key={`prices_${i}`} display={'flex'} gap={2}>
                                    <Typography fontWeight={700}>
                                      {emFormatToCurrency(p.min_price ? p.min_price : 0)}
                                    </Typography>
                                  </Box>
                                )
                              }
                            })}
                          </StyledTableCell>

                          <StyledTableCell align='right'>{row.cart}</StyledTableCell>
                        </StyledTableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </TabPanel>
              <TabPanel value={'team'} sx={{padding: 0, width: '100%'}}>
                {isPersonalTenant ? (
                  <Box>
                    <Card
                      elevation={0}
                      sx={{
                        textAlign: 'center',
                        padding: 3,
                        margin: 0,
                        bgcolor: '#FFF',
                      }}
                    >
                      <Typography variant={'h5'} color={grey[900]}>
                        You are currently in a personal tenant account.
                      </Typography>
                      <Typography variant={'h6'} color={grey[500]}>
                        Switch to a team tenant account to view subscription plans for teams or
                        create a new team tenant.
                      </Typography>
                      <CardContent>
                        <Box
                          display={'flex'}
                          alignContent={'center'}
                          justifyContent={'center'}
                          alignItems={'center'}
                          flexDirection={'column'}
                        >
                          <Button
                            variant='contained'
                            color='primary'
                            onClick={() => makeTeam && makeTeam(true)}
                            sx={{width: '200px', mt: 2}}
                          >
                            Create Team Tenant
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Box>
                ) : queryGetProducts?.isLoading ? (
                  <Box
                    display={'flex'}
                    justifyContent={'center'}
                    p={2}
                    flexDirection={'column'}
                    gap={2}
                  >
                    <Typography variant={'h6'} fontWeight={700}>
                      Loading Plans...
                    </Typography>

                    <LinearProgress sx={{width: '100%', mt: 2}} />
                  </Box>
                ) : (
                  <Table
                    aria-label='customized table'
                    sx={{
                      '& .MuiTableRow-root': {
                        position: 'inherit',
                      },
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        <StyledTableCell width={'12%'}>{'Subscription Plans'}</StyledTableCell>
                        <StyledTableCell width={'25%'}>Prices</StyledTableCell>
                        <StyledTableCell align='right' width={'12%'}>
                          Additional Seats
                        </StyledTableCell>
                        <StyledTableCell align='right' width={'12%'}>
                          <Button
                            sx={{
                              color: blue[900],
                              fontWeight: 700,
                              bgcolor: '#FFFFFF',
                              '&:hover': {bgcolor: grey[50]},
                              position: 'inherit',
                            }}
                            disabled={cartItems?.length === 0}
                            onClick={() => setShowCart(true)}
                            size={'small'}
                            variant={'contained'}
                            startIcon={<ShoppingCartIcon />}
                          >
                            {/* get the number of qty from the array of objects */}
                            View Cart: {cartItems?.length ?? 0}
                          </Button>
                        </StyledTableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {/* put in a tab for basic one for add */}
                      {rows.map((row, i) => {
                        const today = new Date()
                        const modifyDate = new Date(today)
                        const isPurchased = tenants_existing_subscription_plans?.find(
                          (f: any) => f.id === row.id
                        )
                        return (
                          <StyledTableRow key={`rows_${i}`} sx={{position: 'relative'}}>
                            <StyledTableCell component='th' scope='row'>
                              <Typography variant={'body1'} fontWeight={700}>
                                {row.name ?? ''}
                              </Typography>
                              {isPurchased && !isPersonalTenant ? (
                                <Typography variant={'body1'} color={green[600]}>
                                  Currently Subscribed with {isPurchased.total_existing_subs} Seats
                                </Typography>
                              ) : (
                                <></>
                              )}
                            </StyledTableCell>
                            <StyledTableCell>
                              {row.prices?.map((p: any, i2: any) => {
                                return (
                                  <Box key={`prices_team_${i2}`} display={'flex'} gap={2}>
                                    <Typography fontWeight={700}>
                                      {emFormatToCurrency(p.min_price ? p.min_price : 0)}
                                    </Typography>
                                    <Typography fontSize={12} color={'grey.600'}>
                                      for {p.min_seats} {p.min_seats === 1 ? 'seat' : 'seats'}
                                    </Typography>
                                    <Typography fontSize={12} color={'grey.600'}>
                                      and{' '}
                                      {p.per_seat_price ? emFormatToCurrency(p.per_seat_price) : 0}{' '}
                                      per additional Seat
                                    </Typography>
                                  </Box>
                                )
                              })}
                            </StyledTableCell>
                            <StyledTableCell align='right'>{row.seats}</StyledTableCell>
                            <StyledTableCell align='right'>{row.cart}</StyledTableCell>
                          </StyledTableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                )}
              </TabPanel>
            </TabContext>
          </Grid>
        ) : (
          <Grid item xs={12} flex={1} display={'flex'}>
            {(cartItems?.length ?? 0) != 0 ? (
              <Box
                boxShadow={
                  'rgba(9, 30, 66, 0.25) 0px 4px 8px -2px, rgba(9, 30, 66, 0.08) 0px 0px 0px 1px;'
                }
                width={'100%'}
              >
                <Box
                  bgcolor={'white'}
                  alignContent={'center'}
                  display={'flex'}
                  justifyContent={'space-between'}
                  borderBottom={1}
                >
                  <Typography variant={'h5'} fontWeight={700} p={2}>
                    Shopping Cart
                  </Typography>
                  <Button
                    onClick={() => setShowCart(false)}
                    variant={'text'}
                    sx={{
                      mr: 2,
                      color: blue[900],
                      fontWeight: 700,

                      '&:hover': {color: blue[900]},
                    }}
                  >
                    Continue Shopping Plans
                  </Button>
                </Box>

                {cartItems?.map((row: any, i: number) => (
                  <Box key={`carti_${i}`} m={1} p={2}>
                    <Grid container sx={{mb: 2}}>
                      <Box display={'flex'} p={0.5} bgcolor={'white'} width={'100%'}>
                        <Grid item xs={1}>
                          <div
                            style={{
                              display: 'flex',
                              maxWidth: '65px',
                              minWidth: '62px',
                              alignItems: 'center',
                            }}
                          >
                            <img
                              src={emToAbsoluteUrl('/static/logos/iconIScout.png')}
                              alt='logo'
                              style={{
                                display: 'flex',
                                width: '100%',
                                height: 'auto',
                              }}
                            />
                          </div>
                        </Grid>
                        {isPersonalTenant ? (
                          <Grid item xs={8}>
                            {' '}
                            <Typography fontWeight={700}>{row.plan.name}</Typography>
                            <Button
                              endIcon={<DeleteForeverIcon />}
                              onClick={() => handlRemoveFromCart(row.plan.id)}
                            >
                              Remove
                            </Button>
                          </Grid>
                        ) : (
                          <Grid item xs={8}>
                            <Typography fontWeight={700}>{row.plan.name}</Typography>
                            Seats: {row.seat_quantity}
                            <br />
                            {/* <QuantityButton
                              id={row.plan.id}
                              value={row.seat_quantity}
                              onChange={handleChangeQtyInCart}
                            /> */}
                            <Button
                              endIcon={<DeleteForeverIcon />}
                              onClick={() => handlRemoveFromCart(row.plan.id)}
                              sx={{p: 0, mt: 2}}
                            >
                              Remove Item
                            </Button>
                          </Grid>
                        )}
                        <Grid item xs={2}>
                          &nbsp;
                        </Grid>
                        <Grid item xs={1}>
                          <Typography fontWeight={700}>Item Price:</Typography>
                          {emFormatToCurrency(
                            getPlanPrice(row.plan.prices, row.seat_quantity)
                          )}{' '}
                          {row.plan.name.includes('Annual') ? '/yr' : '/mo'}
                        </Grid>
                      </Box>
                    </Grid>
                    <Divider />
                  </Box>
                ))}

                {cartItems && (
                  <Box display={'flex'} justifyContent={'flex-end'} p={2} alignItems={'center'}>
                    <Typography variant={'h6'}>
                      Subtotal: ({cartItems?.reduce((acc: any, item: number) => acc + 1, 0)}{' '}
                      {cartItems?.length > 1 ? 'items' : 'item'})
                    </Typography>
                    <Typography variant={'h6'} fontWeight={700} p={2}>
                      {emFormatToCurrency(
                        cartItems
                          ?.map((x: any) => {
                            return x
                          })
                          .reduce(
                            (acc: any, x: any) =>
                              acc + getPlanPrice(x.plan.prices, x.seat_quantity),
                            0
                          )
                      )}
                    </Typography>
                  </Box>
                )}
                <Box display={'flex'} justifyContent={'flex-end'} p={2} bgcolor={grey[400]}>
                  {cartItems && <PaymentCheckoutPlan items={cartItems} />}
                </Box>
              </Box>
            ) : (
              <Box display={'flex'} flexDirection={'column'} width={'100%'}>
                <Box
                  bgcolor={'white'}
                  display={'flex'}
                  justifyContent={'space-between'}
                  borderBottom={1}
                  width={'100%'}
                >
                  <Typography variant={'h5'} fontWeight={700} p={2}>
                    Shopping Cart
                  </Typography>
                  <Button
                    onClick={() => setShowCart(false)}
                    variant={'text'}
                    sx={{
                      mr: 2,
                      color: blue[900],
                      fontWeight: 700,
                      '&:hover': {color: blue[800]},
                    }}
                  >
                    Continue Shopping Plans
                  </Button>
                </Box>
                <Box>
                  <EmptyStateCard
                    elevation={0}
                    subTitle={'No subscription plan added to your cart'}
                    p={3}
                    m={3}
                    img={'wind'}
                    size={'5em'}
                  />
                </Box>
              </Box>
            )}
          </Grid>
        )}
      </Grid>
    </>
  )
}
